import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Skeleton from 'react-loading-skeleton';
import { themeColor } from '../Theming';

const animation = keyframes`
  0% {
    transform: scale(0.95);
  }
  5% {
    transform: scale(1.1);
  }
  39% {
    transform: scale(0.85);
  }
  45% {
    transform: scale(1);
  }
  60% {
    transform: scale(0.95);
  }
  100% {
    transform: scale(0.9);
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  & > div {
    display: inline-block;
    position: relative;
    width: 80px;
    height: 80px;
    transform: rotate(45deg);
    transform-origin: 40px 40px;

    & div {
      top: 32px;
      left: 32px;
      position: absolute;
      width: 32px;
      height: 32px;
      background: ${themeColor('purple')};
      animation: ${animation} 1.2s infinite cubic-bezier(0.215, 0.61, 0.355, 1);
    }
    & div:after,
    & div:before {
      content: ' ';
      position: absolute;
      display: block;
      width: 32px;
      height: 32px;
      background: ${themeColor('purple')};
    }
    & div:before {
      left: -24px;
      border-radius: 50% 0 0 50%;
    }
    & div:after {
      top: -24px;
      border-radius: 50% 50% 0 0;
    }
  }
`;
const scale = keyframes`
  0%,
  20%,
  80%,
  100% {
    transform: scale(0.5);
  }
  50% {
    transform: scale(1);
  }
`;

const SpinnerContainer = styled.div`
  display: inline-block;
  position: relative;
  width: 40px;
  height: 40px;

  & div {
    position: absolute;
    width: 8px;
    height: 8px;
    background: ${themeColor('darkestGrey')};
    border-radius: 50%;
    animation: ${scale} 1s linear infinite;
  }
  & div:nth-child(1) {
    animation-delay: 0s;
    top: 2px;
    left: 16px;
  }
  & div:nth-child(2) {
    animation-delay: -0.125s;
    top: 6px;
    left: 6px;
  }
  & div:nth-child(3) {
    animation-delay: -0.25s;
    top: 16px;
    left: 2px;
  }
  & div:nth-child(4) {
    animation-delay: -0.375s;
    bottom: 6px;
    left: 6px;
  }
  & div:nth-child(5) {
    animation-delay: -0.5s;
    bottom: 2px;
    left: 16px;
  }
  & div:nth-child(6) {
    animation-delay: -0.625s;
    bottom: 6px;
    right: 6px;
  }
  & div:nth-child(7) {
    animation-delay: -0.75s;
    top: 16px;
    right: 2px;
  }
  & div:nth-child(8) {
    animation-delay: -0.875s;
    top: 6px;
    right: 6px;
  }
`;

interface CoreLoadingIndicatorProps {
  show: boolean;
  className?: string;
}

const HeartLoadingIndicator: React.FC<CoreLoadingIndicatorProps> = ({
  show,
  className,
}) => {
  if (!show) {
    return null;
  }
  return (
    <Container className={className}>
      <div>
        <div></div>
      </div>
    </Container>
  );
};

const SpinnerLoadingIndicator: React.FC<CoreLoadingIndicatorProps> = ({
  show,
  className,
}) => {
  if (!show) {
    return null;
  }
  return (
    <SpinnerContainer className={className}>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </SpinnerContainer>
  );
};

const SkeletonLoadingIndicator: React.FC<CoreLoadingIndicatorProps> = ({
  show,
}) => (show ? <Skeleton /> : <span>&zwnj;</span>);

interface LoadingIndicatorProps {
  type?: 'heart' | 'skeleton' | 'spinner';
  delay?: number;
  className?: string;
}

const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  type = 'heart',
  delay = 200,
  className,
}) => {
  const [show, setShow] = useState(delay === 0);

  useEffect(() => {
    if (delay === 0) {
      return;
    }

    const timeout = setTimeout(() => {
      setShow(true);
    }, delay);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  switch (type) {
    case 'skeleton':
      return <SkeletonLoadingIndicator show={show} />;
    case 'spinner':
      return <SpinnerLoadingIndicator show={show} className={className} />;
    default:
      return <HeartLoadingIndicator show={show} className={className} />;
  }
};

export default LoadingIndicator;
